// 근태 상태 / 휴가 결재 상태 — 색상 구성, StatusIcon, LeaveStatusIcon, 칩
import type { JSX } from 'react';
import type { LeaveStatus } from '@dxtech/shared';

export type WorkStatus = 'working' | 'off' | 'vacation' | 'away' | 'absent' | 'trip' | 'exempt';

export const STATUS: Record<WorkStatus, { bg: string; fg: string; dot: string; label: string }> = {
  working: { bg: '#DCFCE7', fg: '#15803D', dot: '#22C55E', label: '근무중' },
  off: { bg: '#F1F5F9', fg: '#475569', dot: '#64748B', label: '퇴근' },
  vacation: { bg: '#FEF3C7', fg: '#B45309', dot: '#F59E0B', label: '휴가' },
  away: { bg: '#EDE9FE', fg: '#6D28D9', dot: '#8B5CF6', label: '자리비움' },
  absent: { bg: '#F3F4F6', fg: '#6B7280', dot: '#CBD5E1', label: '미출근' },
  trip: { bg: '#E0F2FE', fg: '#0369A1', dot: '#0EA5E9', label: '출장' },
  // 출퇴근 관리 예외(대표이사) — 미출근/출근 대신 중립 표시
  exempt: { bg: '#F1F5F9', fg: '#64748B', dot: '#94A3B8', label: '—' },
};

const svgAttrs = {
  width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};

function StatusGlyph({ s }: { s: WorkStatus }): JSX.Element {
  switch (s) {
    case 'working': return <svg {...svgAttrs}><path d="M20 6 9 17l-5-5" /></svg>;
    case 'off': return <svg {...svgAttrs}><path d="M21 12.8A8.5 8.5 0 1 1 11.2 3 6.6 6.6 0 0 0 21 12.8z" /></svg>;
    case 'vacation': return <svg {...svgAttrs}><circle cx="12" cy="12" r="3.6" /><path d="M12 2.5v2.1M12 19.4v2.1M2.5 12h2.1M19.4 12h2.1M5.3 5.3l1.5 1.5M17.2 17.2l1.5 1.5M18.7 5.3l-1.5 1.5M6.8 17.2l-1.5 1.5" /></svg>;
    case 'away': return <svg {...svgAttrs}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.4V12l3.1 1.9" /></svg>;
    case 'trip': return <svg {...svgAttrs}><rect x="3" y="7.5" width="18" height="12.5" rx="2" /><path d="M8 7.5V5.5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>;
    default: return <svg {...svgAttrs}><path d="M5 12h14" /></svg>;
  }
}

export function StatusIcon({ s }: { s: WorkStatus }): JSX.Element {
  const c = STATUS[s];
  return (
    <span title={c.label} aria-label={c.label}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 9, background: c.bg, color: c.fg, flexShrink: 0 }}>
      <StatusGlyph s={s} />
    </span>
  );
}

export function StatusChip({ s }: { s: WorkStatus }): JSX.Element {
  const c = STATUS[s];
  const dot = s === 'absent'
    ? { width: 7, height: 7, borderRadius: '50%', background: '#fff', border: `1.5px solid ${c.dot}` }
    : { width: 7, height: 7, borderRadius: '50%', background: c.dot };
  return (
    <span className="dx-chip" style={{ background: c.bg, color: c.fg }}>
      <span style={dot} />{c.label}
    </span>
  );
}

const LEAVE: Record<LeaveStatus | '상신', { bg: string; fg: string }> = {
  승인: { bg: '#DCFCE7', fg: '#15803D' },
  승인대기: { bg: '#FEF3C7', fg: '#B45309' },
  상신: { bg: '#DBE7FE', fg: '#1D4ED8' },
  반려: { bg: '#FEE2E2', fg: '#B91C1C' },
  취소: { bg: '#F1F5F9', fg: '#64748B' },
};

const leaveSvg = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

export function LeaveStatusIcon({ s }: { s: LeaveStatus }): JSX.Element {
  const c = LEAVE[s] ?? LEAVE['상신'];
  let glyph: JSX.Element;
  if (s === '승인') glyph = <svg {...leaveSvg} strokeWidth={2.4}><path d="M20 6 9 17l-5-5" /></svg>;
  else if (s === '승인대기') glyph = <svg {...leaveSvg} strokeWidth={2}><circle cx="12" cy="12" r="8.6" /><path d="M12 7.4V12l3 1.8" /></svg>;
  else if (s === '반려') glyph = <svg {...leaveSvg} strokeWidth={2.4}><path d="M6 6l12 12M18 6 6 18" /></svg>;
  else glyph = <svg {...leaveSvg} strokeWidth={2}><path d="M12 19V5" /><path d="M6 11l6-6 6 6" /></svg>;
  return (
    <span title={s} aria-label={s}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 9, background: c.bg, color: c.fg, flexShrink: 0 }}>
      {glyph}
    </span>
  );
}

export function LeaveStatusChip({ s }: { s: LeaveStatus }): JSX.Element {
  const c = LEAVE[s] ?? LEAVE['상신'];
  return <span className="dx-chip" style={{ background: c.bg, color: c.fg }}>{s}</span>;
}
